import { Text } from "@/components/ui/Text";
import { Combobox } from "@/components/ui/Combobox";
import { capitalize } from "@/lib/utils";
import { ToolsetEntry } from "@gram/client/models/components/toolsetentry.js";
import { useListToolsets } from "@gram/client/react-query/listToolsets.js";
import { useEffect, useMemo } from "react";

export function ToolsetDropdown({
  selectedToolset,
  setSelectedToolset,
  placeholder = "Select MCP Server",
  noLabel = false,
  disabledMessage,
  defaultSelection = "most-recent",
}: {
  selectedToolset: ToolsetEntry | undefined;
  setSelectedToolset: (toolset: ToolsetEntry) => void;
  placeholder?: string;
  noLabel?: boolean;
  disabledMessage?: string;
  defaultSelection?: "most-recent";
}): JSX.Element {
  const { data: toolsets } = useListToolsets();

  const toolsetDropdownItems = useMemo(
    () =>
      (
        toolsets?.toolsets?.map((toolset) => ({
          ...toolset,
          label: toolset.name,
          value: toolset.slug,
        })) ?? []
      ).sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
    [toolsets?.toolsets],
  );

  // Set the default selection if no selection is made
  useEffect(() => {
    if (
      defaultSelection === "most-recent" &&
      toolsetDropdownItems.length > 0 &&
      !selectedToolset
    ) {
      setSelectedToolset(toolsetDropdownItems[0]!);
    }
  }, [
    toolsetDropdownItems,
    defaultSelection,
    setSelectedToolset,
    selectedToolset,
  ]);

  return (
    <Combobox
      label={noLabel ? undefined : "Toolset"}
      items={toolsetDropdownItems}
      selected={toolsetDropdownItems.find(
        (item) => item.value === selectedToolset?.slug,
      )}
      onSelectionChange={setSelectedToolset}
      disabledMessage={disabledMessage}
      className="max-w-fit"
    >
      <Text variant="small" className="font-medium">
        {capitalize(selectedToolset?.name ?? placeholder)}
      </Text>
    </Combobox>
  );
}
